import { 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit,
  doc,
  updateDoc,
  deleteDoc,
  getDoc,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from './firebase';
import smsService from './smsService';const COLLECTION_NAME = 'points_history';

// Função para buscar configurações de expiração da empresa
const getCompanyExpirationSettings = async (companyId) => {
  try {
    const { companyService } = await import('./companyService');
    const expirationSettings = await companyService.getExpirationSettings(companyId);
    
    return {
      pointsExpirationEnabled: expirationSettings.enabled,
      pointsExpirationMonths: expirationSettings.months
    };
  } catch (error) {
    console.error('Erro ao buscar configurações de expiração:', error);
    // Em caso de erro, retornar configurações padrão desabilitadas
    return {
      pointsExpirationEnabled: false,
      pointsExpirationMonths: 12
    };
  }
};

// Função para calcular data de expiração
const calculateExpirationDate = (months) => {
  const expirationDate = new Date();
  expirationDate.setMonth(expirationDate.getMonth() + months);
  return Timestamp.fromDate(expirationDate);
};

// Função para sanitizar dados antes de enviar ao Firestore
const sanitizeData = (data) => {
  const sanitized = {};
  
  for (const [key, value] of Object.entries(data)) {
    if (value !== undefined && value !== null) {
      // Converter strings vazias em null
      if (typeof value === 'string' && value.trim() === '') {
        sanitized[key] = null;
      }
      // Converter números para garantir que são válidos
      else if (typeof value === 'number' && !isNaN(value)) {
        sanitized[key] = value;
      }
      // Manter outros tipos de dados válidos
      else if (typeof value === 'string' || typeof value === 'boolean') {
        sanitized[key] = value;
      }
      // Converter Date objects para Timestamp do Firestore
      else if (value instanceof Date) {
        sanitized[key] = Timestamp.fromDate(value);
      }
      // Converter strings de data ISO para Timestamp
      else if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
        sanitized[key] = Timestamp.fromDate(new Date(value));
      }
      else {
        sanitized[key] = value;
      }
    }
  }
  
  // Adicionar campo 'expired' padrão se não existir (para pontos positivos)
  if (sanitized.points > 0 && sanitized.expired === undefined) {
    sanitized.expired = false;
  }
  
  return sanitized;
};

// Função para validar dados obrigatórios para points_history
const validatePointsData = (data) => {
  const required = ['companyId', 'customerId', 'points'];
  const missing = required.filter(field => !data[field] && data[field] !== 0);
  
  if (missing.length > 0) {
    throw new Error(`Campos obrigatórios ausentes: ${missing.join(', ')}`);
  }

  if (typeof data.companyId !== 'string' || data.companyId.trim() === '') {
    throw new Error('companyId deve ser uma string não vazia');
  }

  if (typeof data.customerId !== 'string' || data.customerId.trim() === '') {
    throw new Error('customerId deve ser uma string não vazia');
  }

  if (typeof data.points !== 'number' || isNaN(data.points)) {
    throw new Error('points deve ser um número válido');
  }

  // Validar se type está presente e é uma string válida
  if (!data.type || typeof data.type !== 'string') {
    throw new Error('type deve ser uma string válida');
  }

  return true;
};

export const pointsService = {
  // Adicionar pontos para um cliente
  async addPoints(pointsData) {
    try {
      // Validar dados obrigatórios
      validatePointsData(pointsData);
      
      // Validação específica: referral_bonus deve ser sempre positivo
      if (pointsData.type === 'referral_bonus' && pointsData.points <= 0) {
        throw new Error('Bônus de indicação deve ser positivo');
      }
      
      // Buscar dados do cliente antes da operação (importação dinâmica)
      const { customerService } = await import('./customerService');
      const customer = await customerService.getById(pointsData.customerId);
      if (!customer) {
        throw new Error('Cliente não encontrado');
      }
      
      // Buscar configurações de expiração da empresa
      const expirationSettings = await getCompanyExpirationSettings(pointsData.companyId);
      
      // Calcular data de expiração se habilitado
      let expiresAt = null;
      if (expirationSettings.pointsExpirationEnabled && pointsData.points > 0) {
        expiresAt = calculateExpirationDate(expirationSettings.pointsExpirationMonths);
      }
      
      // Sanitizar dados antes de enviar
      const sanitizedData = sanitizeData({
        ...pointsData,
        type: pointsData.type || 'manual', // Usar 'manual' como padrão ao invés de 'add'
        expiresAt,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      
      // Debug: verificar se referral_bonus está negativo
      if (sanitizedData.type === 'referral_bonus' && sanitizedData.points < 0) {
        console.error('🚨 ERRO: referral_bonus não pode ser negativo!', {
          points: sanitizedData.points,
          originalPoints: pointsData.points,
          customerId: pointsData.customerId,
          description: sanitizedData.description
        });
      }
      
      // Debug: log da importação CSV
      if (sanitizedData.description && sanitizedData.description.includes('CSV')) {
        console.log('🔍 IMPORTAÇÃO CSV DEBUG:', {
          customerId: sanitizedData.customerId,
          points: sanitizedData.points,
          type: sanitizedData.type,
          description: sanitizedData.description
        });
      }
      
      // 1. Adicionar ao histórico de pontos
      const docRef = await addDoc(collection(db, COLLECTION_NAME), sanitizedData);
      
      // 2. Atualizar pontos do cliente
      await customerService.addPoints(pointsData.customerId, pointsData.points);
      
      // 3. Buscar pontos atualizados do cliente
      const updatedCustomer = await customerService.getById(pointsData.customerId);
      const newTotalPoints = updatedCustomer?.points || 0;
      
      // 4. Enviar SMS de pontos adicionados (não bloquear em caso de erro)
      try {
        await smsService.sendPointsAddedSMS(
          pointsData.companyId, 
          customer, 
          pointsData.points, 
          newTotalPoints
        );
      } catch (smsError) {
        console.warn('Erro ao enviar SMS (não crítico):', smsError);
        // Não falhar a operação se SMS falhar
      }
      
      return {
        id: docRef.id,
        ...pointsData,
        createdAt: new Date(),
        updatedAt: new Date()
      };
    } catch (error) {
      console.error('Error adding points:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        pointsData: pointsData
      });
      
      // Melhorar mensagens de erro baseadas no código
      if (error.code === 'permission-denied') {
        throw new Error('Permissão negada: Verifique se você está autenticado e tem permissão para adicionar pontos. Detalhes: ' + error.message);
      } else if (error.code === 'invalid-argument') {
        throw new Error('Dados inválidos: ' + error.message);
      } else if (error.code === 'not-found') {
        throw new Error('Documento não encontrado: ' + error.message);
      } else if (error.message.includes('Campos obrigatórios')) {
        throw error; // Relançar erro de validação
      } else {
        throw new Error('Erro ao adicionar pontos: ' + error.message);
      }
    }
  },

  // Resgatar pontos de um cliente
  async redeemPoints(redeemData) {
    try {
      const dataWithNegativePoints = {
        ...redeemData,
        points: -Math.abs(redeemData.points), // Garantir que seja negativo
        type: 'redeem'
      };
      
      // Validar dados obrigatórios
      validatePointsData(dataWithNegativePoints);
      
      // Buscar dados do cliente antes da operação (importação dinâmica)
      const { customerService } = await import('./customerService');
      const customer = await customerService.getById(redeemData.customerId);
      if (!customer) {
        throw new Error('Cliente não encontrado');
      }
      
      // Sanitizar dados antes de enviar
      const sanitizedData = sanitizeData({
        ...dataWithNegativePoints,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      
      // 1. Adicionar ao histórico de pontos (com valor negativo)
      const docRef = await addDoc(collection(db, COLLECTION_NAME), sanitizedData);
      
      // 2. Decrementar pontos do cliente (usando valor absoluto)
      await customerService.redeemPoints(redeemData.customerId, Math.abs(redeemData.points));

      // 3. Se for um prêmio de aluguel, criar movimentação de aluguel
      if (redeemData.rewardType === 'rental' && redeemData.rewardData) {
        try {
          const { createRentalMovement } = await import('./rentalService');
          
          // Calcular datas automaticamente baseado na data atual e duração padrão
          const today = new Date();
          const deliveryDate = redeemData.rewardData.expectedDeliveryDate ? 
            new Date(redeemData.rewardData.expectedDeliveryDate) : today;
          const expectedReturnDate = new Date(deliveryDate);
          expectedReturnDate.setDate(expectedReturnDate.getDate() + (redeemData.rewardData.rentalDuration || 7));

          const rentalMovementData = {
            rewardId: redeemData.rewardId,
            rewardName: redeemData.rewardName || redeemData.description,
            customerId: redeemData.customerId,
            customerName: customer.name,
            customerDocument: customer.cpf || customer.cnpj,
            rentalDuration: redeemData.rewardData.rentalDuration || 7,
            expectedDeliveryDate: deliveryDate.toISOString().split('T')[0],
            actualDeliveryDate: redeemData.rewardData.actualDeliveryDate || today.toISOString().split('T')[0],
            expectedReturnDate: expectedReturnDate.toISOString().split('T')[0],
            pointsUsed: Math.abs(redeemData.points),
            notes: redeemData.notes || ''
          };
          
          await createRentalMovement(redeemData.companyId, rentalMovementData);
        } catch (rentalError) {
          console.error('Erro ao criar movimentação de aluguel:', rentalError);
          // Não falhar o resgate se houver erro na movimentação
        }
      }
      
      // 4. Buscar pontos atualizados do cliente
      const updatedCustomer = await customerService.getById(redeemData.customerId);
      const newTotalPoints = updatedCustomer?.points || 0;
      
      // 5. Enviar SMS de pontos resgatados (não bloquear em caso de erro)
      try {
        await smsService.sendPointsRedeemedSMS(
          redeemData.companyId, 
          customer, 
          Math.abs(redeemData.points), 
          newTotalPoints
        );
      } catch (smsError) {
        console.warn('Erro ao enviar SMS (não crítico):', smsError);
        // Não falhar a operação se SMS falhar
      }
      
      return {
        id: docRef.id,
        ...dataWithNegativePoints,
        createdAt: new Date(),
        updatedAt: new Date()
      };
    } catch (error) {
      console.error('Error redeeming points:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      if (error.code === 'permission-denied') {
        throw new Error('Permissão negada: Verifique se você está autenticado e tem permissão para resgatar pontos');
      } else if (error.code === 'invalid-argument') {
        throw new Error('Dados inválidos: ' + error.message);
      } else if (error.message.includes('Campos obrigatórios')) {
        throw error;
      } else {
        throw new Error('Erro ao resgatar pontos: ' + error.message);
      }
    }
  },

  // Buscar histórico de pontos por empresa
  async getByCompany(companyId) {
    try {
      if (!companyId) {
        return [];
      }
      
      if (typeof companyId !== 'string') {
        return [];
      }
      
      // Tentar consulta com orderBy primeiro
      try {
        const q = query(
          collection(db, COLLECTION_NAME),
          where('companyId', '==', companyId),
          orderBy('createdAt', 'desc')
        );
        
        const querySnapshot = await getDocs(q);
        const pointsHistory = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          pointsHistory.push({
            id: doc.id,
            ...data,
            // Converter Timestamps de volta para Date se necessário
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
            updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt
          });
        });
        
        return pointsHistory;
        
      } catch (orderError) {
        // Fallback: consulta sem orderBy e ordenação manual
        const q = query(
          collection(db, COLLECTION_NAME),
          where('companyId', '==', companyId)
        );
        
        const querySnapshot = await getDocs(q);
        const pointsHistory = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          pointsHistory.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
            updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt
          });
        });
        
        // Ordenar manualmente por data de criação (mais recente primeiro)
        pointsHistory.sort((a, b) => {
          const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
          const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
          return dateB.getTime() - dateA.getTime();
        });
        
        return pointsHistory;
      }
      
    } catch (error) {
      console.error('Error fetching points by company:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      if (error.code === 'permission-denied') {
        throw new Error('Permissão negada: Verifique se você está autenticado e tem permissão para visualizar o histórico');
      } else if (error.code === 'failed-precondition') {
        throw new Error('Índice do Firestore necessário. Consulte o console do Firebase para criar o índice necessário.');
      } else if (error.message.includes('ID da empresa')) {
        throw error;
      } else {
        throw new Error('Erro ao buscar histórico de pontos da empresa: ' + error.message);
      }
    }
  },

  // Buscar histórico de pontos por cliente
  async getByCustomer(customerId) {
    try {
      if (!customerId || typeof customerId !== 'string') {
        throw new Error('ID do cliente é obrigatório e deve ser uma string');
      }
      
      try {
        const q = query(
          collection(db, COLLECTION_NAME),
          where('customerId', '==', customerId),
          orderBy('createdAt', 'desc')
        );
        
        const querySnapshot = await getDocs(q);
        const pointsHistory = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          pointsHistory.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
            updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt
          });
        });
        
        return pointsHistory;
        
      } catch (orderError) {
        console.warn('OrderBy query failed for customer, trying without orderBy:', orderError);
        
        const q = query(
          collection(db, COLLECTION_NAME),
          where('customerId', '==', customerId)
        );
        
        const querySnapshot = await getDocs(q);
        const pointsHistory = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          pointsHistory.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
            updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt
          });
        });
        
        pointsHistory.sort((a, b) => {
          const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
          const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
          return dateB.getTime() - dateA.getTime();
        });
        
        return pointsHistory;
      }
      
    } catch (error) {
      console.error('Error fetching points by customer:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      if (error.code === 'permission-denied') {
        throw new Error('Permissão negada: Verifique se você está autenticado e tem permissão para visualizar o histórico do cliente');
      } else if (error.code === 'failed-precondition') {
        throw new Error('Índice do Firestore necessário para busca por cliente');
      } else if (error.message.includes('ID do cliente')) {
        throw error;
      } else {
        throw new Error('Erro ao buscar histórico de pontos do cliente: ' + error.message);
      }
    }
  },

  // Buscar todo o histórico de pontos (admin)
  async getAll() {
    try {
      try {
        const q = query(
          collection(db, COLLECTION_NAME),
          orderBy('createdAt', 'desc'),
          limit(1000) // Limitar para performance
        );
        
        const querySnapshot = await getDocs(q);
        const pointsHistory = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          pointsHistory.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
            updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt
          });
        });
        
        return pointsHistory;
        
      } catch (orderError) {
        console.warn('OrderBy query failed for getAll, trying without orderBy:', orderError);
        
        // Fallback: consulta sem orderBy e ordenação manual
        const q = query(
          collection(db, COLLECTION_NAME),
          limit(1000)
        );
        
        const querySnapshot = await getDocs(q);
        const pointsHistory = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          pointsHistory.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
            updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt
          });
        });
        
        // Ordenar manualmente por data de criação (mais recente primeiro)
        pointsHistory.sort((a, b) => {
          const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
          const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
          return dateB.getTime() - dateA.getTime();
        });
        
        return pointsHistory;
      }
      
    } catch (error) {
      console.error('Error fetching all points:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      if (error.code === 'permission-denied') {
        throw new Error('Permissão negada: Verifique se você tem permissão de administrador');
      } else if (error.code === 'failed-precondition') {
        throw new Error('Índice do Firestore necessário para buscar todo o histórico');
      } else {
        throw new Error('Erro ao buscar todo o histórico de pontos: ' + error.message);
      }
    }
  },

  // Buscar transações recentes
  async getRecent(limitCount = 10) {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
      
      const querySnapshot = await getDocs(q);
      const recentTransactions = [];
      
      querySnapshot.forEach((doc) => {
        recentTransactions.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      return recentTransactions;
    } catch (error) {
      console.error('Error fetching recent transactions:', error);
      throw new Error('Erro ao buscar transações recentes');
    }
  },

  // Buscar estatísticas de pontos por empresa
  async getCompanyStats(companyId) {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where('companyId', '==', companyId)
      );
      
      const querySnapshot = await getDocs(q);
      let totalAdded = 0;
      let totalRedeemed = 0;
      let totalTransactions = 0;
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        totalTransactions++;
        
        if (data.type === 'add' || data.points > 0) {
          totalAdded += Math.abs(data.points);
        } else {
          totalRedeemed += Math.abs(data.points);
        }
      });
      
      return {
        totalAdded,
        totalRedeemed,
        netPoints: totalAdded - totalRedeemed,
        totalTransactions
      };
    } catch (error) {
      console.error('Error fetching company stats:', error);
      throw new Error('Erro ao buscar estatísticas da empresa');
    }
  },

  // Atualizar transação de pontos
  async update(transactionId, updateData) {
    try {
      const sanitizedData = sanitizeData({
        ...updateData,
        updatedAt: Timestamp.now()
      });
      
      const docRef = doc(db, COLLECTION_NAME, transactionId);
      await updateDoc(docRef, sanitizedData);
      
      return {
        id: transactionId,
        ...updateData,
        updatedAt: new Date()
      };
    } catch (error) {
      console.error('Error updating points transaction:', error);
      throw new Error('Erro ao atualizar transação de pontos');
    }
  },

  // Deletar transação de pontos
  async delete(transactionId) {
    try {
      const docRef = doc(db, COLLECTION_NAME, transactionId);
      await deleteDoc(docRef);
      return true;
    } catch (error) {
      console.error('Error deleting points transaction:', error);
      throw new Error('Erro ao deletar transação de pontos');
    }
  },

  // Buscar transação por ID
  async getById(transactionId) {
    try {
      const docRef = doc(db, COLLECTION_NAME, transactionId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        };
      } else {
        throw new Error('Transação não encontrada');
      }
    } catch (error) {
      console.error('Error fetching points transaction:', error);
      throw new Error('Erro ao buscar transação de pontos');
    }
  },

  // Alias para getByCustomer para compatibilidade
  async getCustomerHistory(customerId) {
    return this.getByCustomer(customerId);
  },

  // Adicionar histórico de pontos manualmente
  async addHistory(historyData) {
    try {
      // Validar dados obrigatórios
      if (!historyData.customerId || !historyData.companyId) {
        throw new Error('customerId e companyId são obrigatórios');
      }

      if (!historyData.points || typeof historyData.points !== 'number') {
        throw new Error('points deve ser um número válido');
      }

      // Usar description padrão se não fornecida
      const description = historyData.description || historyData.reason || 'Transação de pontos';

      // Sanitizar dados antes de enviar
      const sanitizedData = sanitizeData({
        customerId: historyData.customerId,
        companyId: historyData.companyId,
        points: historyData.points,
        type: historyData.type || (historyData.points > 0 ? 'add' : 'redeem'),
        description: description, // Salvar como description por compatibilidade
        reason: description, // Também salvar como reason para queries alternativas
        rewardId: historyData.rewardId || null,
        redemptionId: historyData.redemptionId || null,
        createdAt: historyData.createdAt ? Timestamp.fromDate(new Date(historyData.createdAt)) : Timestamp.now(),
        updatedAt: Timestamp.now()
      });

      // Adicionar ao histórico
      const docRef = await addDoc(collection(db, COLLECTION_NAME), sanitizedData);
      
      return {
        id: docRef.id,
        ...sanitizedData
      };
    } catch (error) {
      console.error('Error adding points history:', error);
      throw new Error('Erro ao adicionar histórico de pontos: ' + error.message);
    }
  },

  // Buscar pontos válidos do cliente (não expirados)
  async getValidCustomerPoints(customerId, companyId) {
    try {
      // Primeiro, buscar o saldo atual do cliente
      const { customerService } = await import('./customerService');
      const customer = await customerService.getById(customerId);
      const currentPoints = customer.points || 0;
      
      const now = Timestamp.now();
      
      // Buscar todas as transações de pontos (positivos e negativos)
      const q = query(
        collection(db, COLLECTION_NAME),
        where('customerId', '==', customerId),
        where('companyId', '==', companyId),
        orderBy('createdAt', 'asc')
      );

      const querySnapshot = await getDocs(q);
      const allTransactions = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Separar adições e resgates
      const additions = allTransactions.filter(t => t.points > 0);
      const redemptions = allTransactions.filter(t => t.points < 0);
      
      // Calcular total de resgates
      const totalRedemptions = Math.abs(redemptions.reduce((sum, r) => sum + r.points, 0));
      
      // Calcular pontos que irão expirar considerando apenas os que ainda estão válidos
      let pointsToExpire = [];
      let remainingRedemptions = totalRedemptions;
      
      // Ordenar adições por data de criação (FIFO - First In, First Out)
      const sortedAdditions = additions.sort((a, b) => 
        a.createdAt.toMillis() - b.createdAt.toMillis()
      );
      
      // Para cada adição, verificar se ainda há pontos válidos após os resgates
      for (const addition of sortedAdditions) {
        // Se esta adição tem data de expiração e ainda não expirou
        if (addition.expiresAt && addition.expiresAt.toDate() > now.toDate()) {
          let availablePoints = addition.points;
          
          // Se ainda há resgates a serem descontados
          if (remainingRedemptions > 0) {
            if (remainingRedemptions >= availablePoints) {
              // Todos os pontos desta adição foram resgatados
              remainingRedemptions -= availablePoints;
              availablePoints = 0;
            } else {
              // Apenas parte dos pontos desta adição foi resgatada
              availablePoints -= remainingRedemptions;
              remainingRedemptions = 0;
            }
          }
          
          // Se ainda há pontos disponíveis nesta adição, adicionar à lista de expiração
          if (availablePoints > 0) {
            pointsToExpire.push({
              points: availablePoints,
              expiresAt: addition.expiresAt.toDate(),
              reason: addition.reason || addition.description,
              createdAt: addition.createdAt.toDate()
            });
          }
        }
      }

      // Ordenar pontos que vão expirar por data de expiração
      pointsToExpire.sort((a, b) => a.expiresAt - b.expiresAt);

      return {
        validPoints: currentPoints, // Usar o saldo real do cliente
        pointsToExpire
      };
    } catch (error) {
      console.error('Erro ao buscar pontos válidos:', error);
      throw new Error('Erro ao buscar pontos válidos: ' + error.message);
    }
  },

  // Buscar pontos expirados do cliente
  async getExpiredPoints(customerId, companyId) {
    try {
      const now = Timestamp.now();
      
      const q = query(
        collection(db, COLLECTION_NAME),
        where('customerId', '==', customerId),
        where('companyId', '==', companyId),
        where('points', '>', 0),
        where('expiresAt', '<', now),
        orderBy('expiresAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Erro ao buscar pontos expirados:', error);
      return [];
    }
  },

  // Processar expiração automática de pontos (para ser usado em rotinas)
  async processPointsExpiration(companyId) {
    try {
      const now = Timestamp.now();
      
      // Buscar todos os pontos expirados que ainda não foram processados
      const q = query(
        collection(db, COLLECTION_NAME),
        where('companyId', '==', companyId),
        where('points', '>', 0),
        where('expiresAt', '<', now),
        where('expired', '==', false) // Assumindo que vamos adicionar um campo 'expired'
      );

      const querySnapshot = await getDocs(q);
      const expiredEntries = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      let totalExpiredPoints = 0;
      const expirationPromises = [];
      
      // Importação dinâmica do customerService
      const { customerService } = await import('./customerService');

      for (const entry of expiredEntries) {
        totalExpiredPoints += entry.points;
        
        // Marcar como expirado
        const updatePromise = updateDoc(doc(db, COLLECTION_NAME, entry.id), {
          expired: true,
          expiredAt: now,
          updatedAt: now
        });
        expirationPromises.push(updatePromise);

        // Criar registro de expiração no histórico
        const expirationRecord = {
          customerId: entry.customerId,
          companyId: entry.companyId,
          points: -entry.points, // Negativo para indicar perda
          type: 'expired',
          reason: `Pontos expirados - Original: ${entry.reason || entry.description}`,
          originalEntryId: entry.id,
          createdAt: now,
          updatedAt: now
        };

        const addExpirationPromise = addDoc(collection(db, COLLECTION_NAME), expirationRecord);
        expirationPromises.push(addExpirationPromise);

        // Atualizar pontos do cliente (decrementar)
        const updateCustomerPromise = customerService.redeemPoints(entry.customerId, entry.points);
        expirationPromises.push(updateCustomerPromise);
      }

      await Promise.all(expirationPromises);

      return {
        expiredEntriesCount: expiredEntries.length,
        totalExpiredPoints
      };
    } catch (error) {
      console.error('Erro ao processar expiração de pontos:', error);
      throw new Error('Erro ao processar expiração de pontos: ' + error.message);
    }
  },

  // Atualizar pontos existentes com datas de expiração baseadas na configuração da empresa
  async updateExistingPointsWithExpiration(companyId) {
    try {
      // Buscar configurações de expiração da empresa
      const expirationSettings = await getCompanyExpirationSettings(companyId);
      
      if (!expirationSettings.pointsExpirationEnabled) {
        return {
          updatedCount: 0,
          message: 'Empresa não tem expiração habilitada'
        };
      }

      // Buscar todos os pontos da empresa que não têm data de expiração
      const q = query(
        collection(db, COLLECTION_NAME),
        where('companyId', '==', companyId),
        where('points', '>', 0) // Apenas pontos positivos (adições)
      );

      const querySnapshot = await getDocs(q);
      const pointsWithoutExpiration = [];
      
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        // Se não tem expiresAt ou é null/undefined
        if (!data.expiresAt) {
          pointsWithoutExpiration.push({
            id: doc.id,
            ...data
          });
        }
      });

      if (pointsWithoutExpiration.length === 0) {
        return {
          updatedCount: 0,
          message: 'Todos os pontos já possuem data de expiração'
        };
      }

      // Atualizar cada entrada com data de expiração
      const updatePromises = pointsWithoutExpiration.map(async (entry) => {
        try {
          // Calcular data de expiração baseada na data de criação do ponto
          const createdAt = entry.createdAt.toDate();
          const expirationDate = new Date(createdAt);
          expirationDate.setMonth(expirationDate.getMonth() + expirationSettings.pointsExpirationMonths);
          
          const expiresAt = Timestamp.fromDate(expirationDate);
          
          // Atualizar o documento
          const docRef = doc(db, COLLECTION_NAME, entry.id);
          await updateDoc(docRef, {
            expiresAt: expiresAt,
            updatedAt: Timestamp.now()
          });

          return true;
        } catch (error) {
          console.error(`Erro ao atualizar ponto ${entry.id}:`, error);
          return false;
        }
      });

      const results = await Promise.all(updatePromises);
      const successCount = results.filter(result => result === true).length;

      return {
        updatedCount: successCount,
        totalFound: pointsWithoutExpiration.length,
        message: `${successCount} pontos atualizados com sucesso`
      };
      
    } catch (error) {
      console.error('Erro ao atualizar pontos com expiração:', error);
      throw new Error('Erro ao atualizar pontos com expiração: ' + error.message);
    }
  },

  // Atualizar pontos de todas as empresas com expiração habilitada
  async updateAllCompaniesPointsWithExpiration() {
    try {
      const { companyService } = await import('./companyService');
      const companies = await companyService.getAll();
      
      const companiesWithExpiration = companies.filter(company => 
        company.pointsExpiration?.enabled === true
      );

      if (companiesWithExpiration.length === 0) {
        return {
          processedCompanies: 0,
          totalUpdated: 0,
          message: 'Nenhuma empresa com expiração habilitada'
        };
      }

      let totalUpdated = 0;
      let processedCompanies = 0;

      for (const company of companiesWithExpiration) {
        try {
          const result = await this.updateExistingPointsWithExpiration(company.id);
          totalUpdated += result.updatedCount;
          processedCompanies++;
        } catch (error) {
          console.error(`❌ Erro ao processar empresa ${company.name}:`, error);
          // Continuar com outras empresas mesmo se uma falhar
        }
      }

      return {
        processedCompanies,
        totalUpdated,
        companiesWithExpiration: companiesWithExpiration.length,
        message: `${totalUpdated} pontos atualizados para expiração em ${processedCompanies} empresas`
      };
    } catch (error) {
      console.error('❌ Erro ao atualizar pontos em lote:', error);
      throw new Error('Erro ao processar expiração para todas as empresas: ' + error.message);
    }
  },

  // Função para limpar registros de referral_bonus negativos incorretos
  async cleanupNegativeReferralBonus(companyId) {
    try {
      const pointsRef = collection(db, COLLECTION_NAME);
      const q = query(
        pointsRef,
        where('companyId', '==', companyId),
        where('type', '==', 'referral_bonus'),
        where('points', '<', 0)
      );
      
      const snapshot = await getDocs(q);
      const batch = writeBatch(db);
      
      let count = 0;
      snapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
        count++;
      });
      
      if (count > 0) {
        await batch.commit();
        console.log(`🧹 Removidos ${count} registros de referral_bonus negativos incorretos`);
      }
      
      return count;
    } catch (error) {
      console.error('Error cleaning up negative referral bonus:', error);
      throw new Error('Erro ao limpar registros de bônus negativos');
    }
  },

  // Buscar informações de expiração de pontos para um cliente
  async getExpirationInfo(customerId) {
    try {
      const pointsRef = collection(db, COLLECTION_NAME);
      const now = new Date();
      
      // Buscar pontos que irão expirar nos próximos 30 dias
      const next30Days = new Date();
      next30Days.setDate(now.getDate() + 30);
      
      const expiringQuery = query(
        pointsRef,
        where('customerId', '==', customerId),
        where('type', '==', 'earned'),
        where('expirationDate', '>', Timestamp.fromDate(now)),
        where('expirationDate', '<=', Timestamp.fromDate(next30Days)),
        orderBy('expirationDate', 'asc')
      );
      
      const expiringSnapshot = await getDocs(expiringQuery);
      
      let pointsExpiringInNext30Days = 0;
      let nextExpirationDate = null;
      let nextExpirationPoints = 0;
      
      expiringSnapshot.forEach(doc => {
        const data = doc.data();
        pointsExpiringInNext30Days += data.points || 0;
        
        if (!nextExpirationDate) {
          nextExpirationDate = data.expirationDate;
          nextExpirationPoints = data.points || 0;
        }
      });
      
      // Buscar pontos já expirados (para histórico)
      const expiredQuery = query(
        pointsRef,
        where('customerId', '==', customerId),
        where('type', '==', 'earned'),
        where('expirationDate', '<=', Timestamp.fromDate(now)),
        orderBy('expirationDate', 'desc'),
        limit(5)
      );
      
      const expiredSnapshot = await getDocs(expiredQuery);
      const expiredPoints = [];
      
      expiredSnapshot.forEach(doc => {
        const data = doc.data();
        expiredPoints.push({
          id: doc.id,
          points: data.points || 0,
          expirationDate: data.expirationDate,
          earnedDate: data.timestamp
        });
      });
      
      return {
        pointsExpiringInNext30Days,
        nextExpirationDate,
        nextExpirationPoints,
        expiredPoints,
        hasExpirationEnabled: true // Pode ser configurável no futuro
      };
    } catch (error) {
      console.error('Erro ao buscar informações de expiração:', error);
      return {
        pointsExpiringInNext30Days: 0,
        nextExpirationDate: null,
        nextExpirationPoints: 0,
        expiredPoints: [],
        hasExpirationEnabled: false
      };
    }
  }
};